import pino, { type Level } from 'pino'
import * as Sentry from '@sentry/react'
import type { SeverityLevel } from '@sentry/react'
import { useEffect } from 'react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

const USER_ERROR_MESSAGES = [
	'user rejected request',
	'user denied request',
	'rejected the request',
	'wallet request rejected',
	'request rejected',
	'user closed modal',
	'user cancelled'
]

if (import.meta.env.PROD) {
	Sentry.init({
		environment: import.meta.env.VITE_ENV === 'mainnet' ? 'production' : import.meta.env.VITE_ENV,
		dsn: import.meta.env.VITE_SENTRY_DSN,
		integrations: [
			Sentry.reactRouterV6BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes
			}),
			Sentry.replayIntegration()
		],
		tracesSampleRate: 1.0,
		tracePropagationTargets: [/^https?:\/\/(?:[a-zA-Z0-9-]+\.)+chiliz\.com/],
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
		beforeSend(event, hint) {
			return isUserRejectionError(hint?.originalException) ? null : event
		}
	})
}

function mapPinoLevelToSentryLevel(level: Level): SeverityLevel {
	return (
		{
			warn: 'warning',
			trace: 'debug'
		}[level] ?? level
	)
}

function normalizePinoError(error: any): Error {
	if (error instanceof Error) {
		return error
	}

	if (error && typeof error === 'object') {
		const { msg = 'Unknown error', stack } = error
		const newError = new Error(msg)
		if (stack) newError.stack = stack

		return newError
	}

	return new Error(String(error))
}

export function sendToSentry(level: Level, logEvent: pino.LogEvent) {
	const { messages, ts } = logEvent
	const [arg1, arg2, ...rest] = messages

	const _level = mapPinoLevelToSentryLevel(level)

	if (typeof arg1 === 'string') {
		if (isUserRejectionError(new Error(arg1))) return

		Sentry.captureMessage(arg1, {
			level: _level,
			extra: {
				timestamp: ts,
				...(typeof arg2 === 'object' && arg2 !== null ? arg2 : {}),
				...(rest.length > 0 ? { additionalData: rest } : {})
			}
		})
	} else {
		const err = normalizePinoError(arg1)

		Sentry.captureException(err, {
			level: _level,
			extra: {
				timestamp: ts,
				...(typeof arg2 === 'string' ? { message: arg2 } : {}),
				...(rest.length > 0 ? { additionalData: rest } : {})
			}
		})
	}
}

function isUserRejectionError(error: unknown): boolean {
	if (!(error instanceof Error)) return false

	const msg = error.message.toLowerCase()
	const name = (error as any).name?.toLowerCase?.()

	return USER_ERROR_MESSAGES.some(userError => msg.includes(userError))
}
